/**
 * Write a finished local run to IndexedDB. **Z-0.**
 *
 * ⚠️ **Nothing did this until 2026-08-02, and it is the defect underneath every other one.**
 * `useLocalMatch` ran the match in memory and handed the result to the run screen. No app code
 * called `addSource`, `putRecords`, `startRun` or `saveResult` — anywhere. So the Dashboard, Sources,
 * Findings, Actions and Matches screens all read a database a real visitor never filled: they showed
 * their empty states forever, and the product looked like one upload box with nothing behind it.
 *
 * The store had existed for weeks. TODO recorded the gap (B-129) as *"already closed by code —
 * `store.ts` is IndexedDB mirroring the tenant schema"*, which was true about the store and false
 * about the write path. Same shape as the executor, the sweep and the audit chain: built, tested,
 * and imported by nothing.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT IS WRITTEN, AND WHY THE RAW FILE IS AMONG IT
 *
 * The canonical records are what the engine matched on. The **raw rows and headers** are kept too,
 * because `buildCleanedCsv` writes surviving values back into the file's *own* columns — including
 * the ones nothing scored. Without them an export can only run in the same session as the upload,
 * which is why the download button lived on the run screen and nowhere else.
 *
 * ⚠️ It is a second copy of the file. That cost is real and bounded by the per-file cap the run
 * already enforces, and the storage notice warns before a run that would not fit. The alternative —
 * exporting only the four matched fields — would hand back a file worse than the one uploaded.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * IDS ARE NAMESPACED BEFORE THE ENGINE RUNS, NOT HERE
 *
 * `toRecords` numbers rows from 0 within a file, so two files would both produce `r0` and the
 * second load would silently overwrite the first — `putRecords` is an idempotent `put` keyed on
 * `id`. The first version of this module namespaced ids at write time, which worked only as long as
 * a run never contained a record from an earlier file. The moment the match runs over **everything
 * on the device** (which is the Dashboard's stated promise), the result references old ids and new
 * ids in the same pair, and a blanket prefix would corrupt exactly the cross-file pairs the union
 * exists to find. So the caller namespaces the new file's records under `sourceId` *before*
 * matching, and this module writes what it is given.
 *
 * Entity and pair ids are still prefixed with the run id here — the engine names them `e0`, `p0`…
 * per run, so without it a second run's entities would overwrite the first's in the store.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ONE RUN PER LOAD, AND SOURCES ACCUMULATE
 *
 * Each load creates a new `source`; records from earlier loads stay. That is deliberate: matching
 * across everything on the device is the point, and it is what makes the cross-file gap report
 * possible. A run is one matching pass over everything, so the newest run is the current answer —
 * which is exactly what `latestRun()` returns.
 */
import type { MatchResult } from '@dedupr/match-core';
import type { MappingProposal } from '@dedupr/connectors';
import type { ParsedCsv } from './csv.js';
import { openLocalStore, type StoredEntity, type StoredPair, type StoredRecord } from './store.js';
import { emit, emitOnce } from '../analytics.js';
import { recordBand } from '@dedupr/analytics';
import { recordDecision } from './decide.js';

export interface PersistInput {
  /** The file's own name, so the Sources screen can say which file this was. */
  fileName: string;
  parsed: ParsedCsv;
  mapping: MappingProposal;
  /** The id the new records were already namespaced under, minted by the caller before matching. */
  sourceId: string;
  /** The NEW file's records only — ids already `${sourceId}:…`. Earlier files' records are in the store. */
  records: readonly StoredRecord[];
  /**
   * id → source_id for every record the engine saw, old and new. What makes `cross_source` a fact
   * rather than a guess: a pair whose two sides resolve to different sources spans files, and the
   * review screen renders a different control for it (reconcile, never merge — M4 rule 7).
   */
  sourceOf: ReadonlyMap<string, string>;
  result: MatchResult;
  /** Injected so a test can pin the ids and timestamps rather than assert on a clock. */
  now?: () => string;
  newId?: () => string;
}

export interface PersistOutcome {
  sourceId: string;
  runId: string;
  records: number;
  pairs: number;
  entities: number;
}

/**
 * Persist a completed run.
 *
 * Throws on failure rather than swallowing: a run the visitor believes was saved and was not is the
 * one outcome worse than a visible error, because they close the tab.
 */
export async function persistRun(input: PersistInput): Promise<PersistOutcome> {
  const now = input.now ?? (() => new Date().toISOString());
  const newId = input.newId ?? (() => crypto.randomUUID());

  const { sourceId } = input;
  const runId = newId();
  const at = now();

  const pairs: StoredPair[] = input.result.pairs.map((p, i) => ({
    ...p,
    id: `${runId}:p${i}`,
    run_id: runId,
    cross_source: input.sourceOf.get(p.record_a_id) !== input.sourceOf.get(p.record_b_id),
  })) as StoredPair[];

  const entities: StoredEntity[] = input.result.entities.map((e) => ({
    ...e,
    id: `${runId}:${e.id}`,
    run_id: runId,
  })) as StoredEntity[];

  const columnOf: Record<string, number | undefined> = {};
  for (const f of input.mapping.fields) if (f.column !== null) columnOf[f.field] = f.column;

  const store = await openLocalStore();
  try {
    await store.addSource(
      {
        id: sourceId,
        provider: 'csv',
        display_name: input.fileName,
        row_count: input.parsed.rows.length,
        ingested_at: at,
      },
      [...input.records]
    );
    await store.putRaw({
      source_id: sourceId,
      headers: [...input.parsed.headers],
      rows: input.parsed.rows.map((r) => [...r]),
      columnOf,
    });
    /* ⚠️ **`matching`, NOT `complete` — and this was a real defect.** The row was written as
       `complete` here, before `saveResult` had written a single pair, so a crash or a closed tab in
       between left a run the whole app believed had finished with nothing behind it: every screen
       reporting zero duplicates on a file that has them. The comment fifty lines below already said
       *"marked complete only after everything landed"*, and the guard it describes could not hold
       while this line claimed otherwise first (ADR-067 (1)). */
    await store.startRun({
      id: runId,
      started_at: at,
      /* Null until it finishes. A finish time on an unfinished run is what makes an interrupted one
         indistinguishable from a completed one on every screen that shows "last run". */
      finished_at: null,
      engine_version: input.result.stats.engine_version,
      stage: 'matching',
      stats: input.result.stats,
      error: null,
    });
    await store.saveResult(runId, pairs, entities);

    /* ⚠️ **Decisions are carried forward onto the new run's pairs — a human verdict outlives the
       run it was recorded against.** Pair ids are minted per run, and decisions are keyed by pair
       id, so without this every upload silently reset every same/different call ever made: the
       review queue went back to "0 decided", the decision log emptied, and Actions forgot its
       proposals. Found on screen, in the groups report's `decided_pairs 0` on a group a person had
       decided three times.
       The durable identity of a decision is the unordered RECORD pair — record ids persist across
       runs — so the verdict is re-attached wherever the new run proposes the same two records.
       Where several old runs decided the same pair, the most recent verdict wins. */
    const [priorPairs, priorDecisions] = await Promise.all([store.allPairs(), store.decisions()]);
    const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
    const keyOf = new Map(priorPairs.map((p) => [p.id, pairKey(p.record_a_id, p.record_b_id)]));
    const decidedByKey = new Map<string, (typeof priorDecisions)[number]>();
    for (const d of [...priorDecisions].sort((x, y) => (x.decided_at < y.decided_at ? -1 : 1))) {
      const key = keyOf.get(d.pair_id);
      if (key) decidedByKey.set(key, d);
    }
    for (const p of pairs) {
      const carried = decidedByKey.get(pairKey(p.record_a_id, p.record_b_id));
      if (carried && carried.pair_id !== p.id) {
        /* A carry-forward is its own audited fact (`pair.decision_carried`, actor `local:system`) —
           without the event, the trail shows a decision on the OLD pair id while the store holds it
           on the new one, and the chain verifies while the story does not line up. */
        await recordDecision(store, { ...carried, pair_id: p.id }, { carried: true });
      }
    }

    /* Marked complete only after everything landed. A run row that says `complete` while its pairs
       failed to write would make every screen report zero duplicates on a file that has them. */
    await store.finishRun(runId, { finished_at: now(), stage: 'complete' });

    /* ── F15-C: the funnel's steps 3 and 4-input, from the one place a local run lands ──────────
       ⚠️ **Bands, never counts.** `recordBand(1240)` is a fact about the shape of an estate;
       `1240` is a fact about the estate. That distinction is what lets local mode emit at all —
       nothing here is about the records, only about their number's order of magnitude. */
    emitOnce('first_sync_complete', {
      source_type: 'csv',
      record_count_band: recordBand(input.records.length),
    });
    emit('findings_report_generated', {
      pairs_band: recordBand(pairs.length),
      entities_band: recordBand(entities.length),
    });

    return { sourceId, runId, records: input.records.length, pairs: pairs.length, entities: entities.length };
  } finally {
    store.close();
  }
}
